import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button, Input, Card, Sheet } from "../components/ui";
import { Users, PlusCircle, KeyRound, Lock, AlertCircle, Info, X, Users2, ArrowRight } from "lucide-react";

export default function ClubGate() {
  const nav = useNavigate();
  const { user, refreshMe } = useAuth();
  const [tab, setTab] = useState("create");
  const [clubName, setClubName] = useState("");
  const [upi, setUpi] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showJoinError, setShowJoinError] = useState(false);
  const [joinErrorDetail, setJoinErrorDetail] = useState(null);

  useEffect(() => {
    if (user && user.active_club_id) {
      nav("/home", { replace: true });
    }
  }, [user, nav]);

  const createClub = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/clubs", { name: clubName.trim(), upi_id: upi.trim() || null });
      await refreshMe();
      toast.success("Club created!");
      nav("/home", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

const joinClub = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/clubs/join", { join_code: joinCode.trim().toUpperCase() });
      await refreshMe();
      toast.success("Joined club!");
      nav("/home", { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.upgrade_required) {
        const message = detail.message || "Club is already full";
        const currentPlan = detail.current_plan || "current plan";
        const maxMembers = detail.max_members || 0;
        const currentMembers = detail.current_members || 0;
        
        setJoinErrorDetail({
          message,
          currentPlan,
          maxMembers,
          currentMembers: detail.current_members || 0
        });
        setShowJoinError(true);
      } else {
        toast.error(apiError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="app-shell flex flex-col pb-10">
      <div className="px-6 pt-14 pb-4">
        <div className="h-14 w-14 rounded-3xl bg-brand text-white grid place-items-center mb-4">
          <Users size={26} strokeWidth={2} />
        </div>
        <h1 className="text-3xl font-bold">Set up your club</h1>
        <p className="text-ink-muted mt-1 text-[15px]">
          Create a new cricket club or join an existing one with a code.
        </p>
      </div>

      <div className="px-5">
        <div className="grid grid-cols-2 gap-2 mb-4 p-1 bg-black/[0.04] rounded-2xl">
          <button
            onClick={() => setTab("create")}
            data-testid="tab-create-club"
            className={`h-11 rounded-xl text-sm font-semibold ${tab === "create" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            Create
          </button>
          <button
            onClick={() => setTab("join")}
            data-testid="tab-join-club"
            className={`h-11 rounded-xl text-sm font-semibold ${tab === "join" ? "bg-white shadow-sm text-ink" : "text-ink-muted"}`}
          >
            Join
          </button>
        </div>

        {tab === "create" ? (
          <Card>
            <form onSubmit={createClub} className="space-y-4">
              <Input
                label="Club Name"
                placeholder="Sunday Warriors CC"
                value={clubName}
                onChange={(e) => setClubName(e.target.value)}
                required
                data-testid="club-name-input"
              />
              <Input
                label="Club UPI ID (optional)"
                placeholder="club@upi"
                value={upi}
                onChange={(e) => setUpi(e.target.value)}
                data-testid="club-upi-input"
              />
              <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="club-create-btn">
                <PlusCircle size={18} />
                {loading ? "Creating…" : "Create club"}
              </Button>
            </form>
          </Card>
        ) : (
          <Card>
            <form onSubmit={joinClub} className="space-y-4">
              <Input
                label="Join Code"
                placeholder="e.g. ABCD123"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                required
                maxLength={10}
                data-testid="club-join-code-input"
              />
              <p className="text-xs text-ink-muted">
                Ask your club owner for the 7-character invite code.
              </p>
              <Button type="submit" size="lg" className="w-full" disabled={loading} data-testid="club-join-btn">
                <KeyRound size={18} />
                {loading ? "Joining…" : "Join club"}
              </Button>
            </form>
          </Card>
        )}
      </div>
    </div>
    {showJoinError && joinErrorDetail && (
      <div className="fixed inset-0 z-50" data-testid="join-error-modal-overlay">
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setShowJoinError(false)}
          aria-hidden
        />
        <div className="absolute inset-0 flex items-center justify-center p-5">
        <div className="bg-white rounded-3xl p-6 pb-8 w-full max-w-[400px] shadow-xl animate-in zoom-in-95 fade-in duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink">Club is Full</h3>
            <button
              onClick={() => setShowJoinError(false)}
              className="h-9 w-9 rounded-full bg-black/5 hover:bg-black/10"
              data-testid="join-error-modal-close"
              aria-label="Close"
            >
              <X size={24} className="text-ink-muted" />
            </button>
          </div>
          <div className="text-center mb-6">
            <div className="h-16 w-16 rounded-2xl bg-brand-100 grid place-items-center mx-auto mb-4">
              <Lock size={32} className="text-brand" />
            </div>
            <h3 className="font-bold text-lg text-ink mb-1">Club is Full</h3>
            <p className="text-sm text-ink-muted mb-4">{joinErrorDetail.message}</p>
            <p className="text-sm text-ink-muted mb-4">This club is on the {joinErrorDetail.currentPlan} plan.</p>
            <p className="text-sm text-ink-muted">Current members: {joinErrorDetail.currentMembers}/{joinErrorDetail.maxMembers}</p>
          </div>
          <div className="space-y-3">
            <Button
              variant="primary"
              size="lg"
              className="w-full"
              onClick={() => { setShowJoinError(false); nav("/pricing", { replace: true }); }}
              data-testid="join-error-modal-view-plans"
            >
              <ArrowRight size={20} /> View Plans
            </Button>
            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={() => setShowJoinError(false)}
              data-testid="join-error-modal-cancel"
            >
              OK
            </Button>
          </div>
        </div>
        </div>
      </div>
    )}
    </>
  );
}
